import React, { useState } from 'react';

const tabs = [
  ['enabled', 'Enabled'],
  ['campaign', 'Campaigns'],
  ['social', 'Social'],
  ['default', 'Default Ads'],
  ['menu', 'Menu Items'],
  ['missing', 'Missing Artwork'],
];

const categoryForAd = (ad) => ad.has_artwork === false ? 'missing' : ad.source === 'overlay' ? 'campaign' : ad.source;

export default function RotatingAdPicker({ ads, selectedIds, automatic, onChange, readOnly = false, idPrefix = readOnly ? 'rotation-summary' : 'rotation' }) {
  const [activeTab, setActiveTab] = useState('enabled');
  const selected = new Set(selectedIds.map(String));
  const isEnabled = (ad) => automatic ? ad.has_artwork !== false : selected.has(String(ad.id));
  const adsForTab = (key) => ads.filter((ad) => key === 'enabled' ? isEnabled(ad) : categoryForAd(ad) === key);
  const visible = adsForTab(activeTab);
  const updateVisible = (include) => {
    const next = new Set(selected);
    visible.forEach((ad) => include ? next.add(String(ad.id)) : next.delete(String(ad.id)));
    onChange(Array.from(next));
  };

  return (
    <div className="grid min-w-0 w-full max-w-full gap-3">
      <div role="tablist" aria-label="Ad categories" className="flex flex-wrap gap-2">
        {tabs.map(([key, label]) => (
          <button key={key} type="button" role="tab" id={`${idPrefix}-tab-${key}`}
            aria-selected={activeTab === key} aria-controls={`${idPrefix}-ad-panel`}
            onClick={() => setActiveTab(key)}
            className={`rounded-xl px-3 py-2 text-sm ${activeTab === key ? 'bg-white/15 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10'}`}>
            {label} <span className="text-xs opacity-60">{adsForTab(key).length}</span>
          </button>
        ))}
      </div>
      {activeTab === 'missing' && <p className="text-xs text-slate-400">Add artwork to these items to make them available for rotation.</p>}
      {!automatic && !readOnly && (
        <div className="flex gap-2">
          {activeTab !== 'missing' && activeTab !== 'enabled' && <button type="button" className="rounded-xl bg-white/10 px-3 py-2 text-xs text-white" onClick={() => updateVisible(true)}>Select all in tab</button>}
          <button type="button" className="rounded-xl bg-white/10 px-3 py-2 text-xs text-white" onClick={() => updateVisible(false)}>Clear tab</button>
        </div>
      )}
      <div role="tabpanel" id={`${idPrefix}-ad-panel`} aria-labelledby={`${idPrefix}-tab-${activeTab}`} className="min-w-0 w-full max-w-full max-h-[420px] overflow-y-auto overflow-x-hidden rounded-xl bg-black/20 p-2 ring-1 ring-white/10 grid gap-2">
        {visible.map((ad) => (
          <label key={ad.id} className="flex min-w-0 w-full items-center justify-between gap-3 rounded-xl bg-white/5 px-3 py-2 ring-1 ring-white/10">
            <div className="min-w-0 flex-1 overflow-hidden">
              <div className="text-sm font-medium text-white truncate" title={ad.name}>{ad.name}</div>
              {ad.menu_title && <div className="text-xs text-slate-400 truncate">{ad.menu_title}{ad.price_text ? ` · ${ad.price_text}` : ''}</div>}
              {activeTab === 'missing' && <div className="text-xs text-slate-400">{tabs.find(([key]) => key === (ad.source === 'overlay' ? 'campaign' : ad.source))?.[1]}</div>}
              {ad.preview_url && ad.has_artwork !== false && <a className="text-xs text-rose-300" href={ad.preview_url} target="_blank" rel="noreferrer">Preview showcase</a>}
            </div>
            <input type="checkbox" className="shrink-0 accent-rose-500" disabled={automatic || readOnly || (ad.has_artwork === false && !selected.has(String(ad.id)))}
              checked={automatic ? ad.has_artwork !== false : selected.has(String(ad.id))}
              onChange={(event) => {
                const next = new Set(selected);
                if (event.target.checked) next.add(String(ad.id));
                else next.delete(String(ad.id));
                onChange(Array.from(next));
              }} />
          </label>
        ))}
        {!visible.length && <div className="px-3 py-6 text-center text-sm text-slate-400">{activeTab === 'enabled' ? 'No content enabled in this rotation. Choose another tab to add ads.' : activeTab === 'missing' ? 'All items have artwork.' : `No enabled ${tabs.find(([key]) => key === activeTab)[1].toLowerCase()} found for this site.`}</div>}
      </div>
    </div>
  );
}
